import { useTranslation } from 'react-i18next';
import { useQuery, UseQueryResult } from 'react-query';
import {
  DeviceStore,
  SupportedDevice,
  IntegrationType as IntegrationTypeLib,
  Language,
} from 'lavva.webbluetooth/build/js/DeviceStore';
import { useInstallationContext } from '../../context/installation';
import { IntegrationType } from '../../data-access/gql-types/graphql';
import { environment } from '../../environment';
import { IntegrationDefaultEnum } from '../../types/common';

const getStoreIntegrationType = (integrationDefault: IntegrationDefaultEnum, integrationType: IntegrationType) => {
  switch (integrationType) {
    case IntegrationType.Exalus:
      return IntegrationTypeLib.Exalus;
    case IntegrationType.Lavva:
      return integrationDefault === IntegrationDefaultEnum.Wisniowski
        ? IntegrationTypeLib.Wisniowski
        : IntegrationTypeLib.Lavva;
    default:
      return IntegrationTypeLib.Lavva;
  }
};

export const useSupportedDevices = (): UseQueryResult<SupportedDevice[]> => {
  const { i18n } = useTranslation();
  const { integrationType } = useInstallationContext();
  const integration = getStoreIntegrationType(environment.INTEGRATION_DEFAULT, integrationType);

  return useQuery<SupportedDevice[]>(
    ['supported-devices', integrationType, i18n.language],
    async () => {
      const deviceStore = new DeviceStore();
      const supportedDevices = await deviceStore.GetSupportedDevicesAsync(integration, i18n.language as Language);
      return supportedDevices;
    },
    {
      retry: false,
      enabled: integrationType !== IntegrationType.Undefined,
    },
  );
};

export const useSupportedDeviceById = (id?: string): UseQueryResult<SupportedDevice | null> => {
  const { i18n } = useTranslation();
  const { integrationType } = useInstallationContext();
  const integration = getStoreIntegrationType(environment.INTEGRATION_DEFAULT, integrationType);

  return useQuery<SupportedDevice | null>(
    ['supported-device-by-id', id, integrationType, i18n.language],
    async () => {
      const deviceStore = new DeviceStore();
      const supportedDevices = await deviceStore.GetSupportedDevicesAsync(integration, i18n.language as Language);

      return supportedDevices.find((x) => x.ModelGuid === id) || null;
    },
    {
      retry: false,
      enabled: !!id && integrationType !== IntegrationType.Undefined,
    },
  );
};
